import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { PenSquare, ShieldCheck } from 'lucide-react';
import { LoginSchema } from '@blogforge/shared';
import type { LoginInput } from '@blogforge/shared';
import { api, ApiError } from '../hooks/api.js';
import { useAuthStore } from '../stores/authStore.js';

/**
 * Capability grid. These are the six things the page has always claimed,
 * re-cut into the Feature Card rhythm: 18px/600 title, 14px bark-grey body,
 * and a hairline footer carrying a mono micro-label.
 *
 * The footer is deliberately a tag row rather than a "DOCS →" arrow link —
 * there is no public docs URL yet, and an arrow link is a promise that you
 * can go somewhere. Once docs exist, swap `meta` for an href and render it
 * as `<a className="arrow-link">`.
 */
const CAPABILITIES = [
  {
    title: 'Visual editor',
    body: 'Drag-and-drop blocks that compile to clean, dependency-free HTML you can host anywhere.',
    meta: 'HTML · CSS',
  },
  {
    title: 'Portable output',
    body: 'Move your content between JSON, HTML and Markdown whenever you like. No runtime, no lock-in.',
    meta: 'JSON · HTML · MD',
  },
  {
    title: 'SEO engine',
    body: 'Meta tags, Open Graph, JSON-LD and sitemaps generated as you write, not bolted on afterwards.',
    meta: 'META · OG · JSON-LD',
  },
  {
    title: 'On-brand email',
    body: 'Send campaigns that look like your site, down to the typeface and the spacing between blocks.',
    meta: 'EMAIL',
  },
  {
    title: 'AI drafting',
    body: 'Ask for a draft and get on-brand copy you can then edit block by block, or not at all.',
    meta: 'AI',
  },
  {
    title: 'Component library',
    body: 'Save any block as a reusable component and drop it into every future post and email.',
    meta: 'BLOCKS',
  },
];

export function LoginPage() {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const [serverError, setServerError] = useState<string | null>(null);

  /**
   * An unclaimed deployment has no accounts to sign in with, so send the
   * visitor to the first-run wizard instead of showing a form that cannot
   * succeed. `GET /api/setup/status` is the authority on what "unclaimed"
   * means (it is the same call that closes the wizard), so no second rule
   * about it is written here.
   */
  const setupStatus = useQuery({
    queryKey: ['instance-setup-status'],
    queryFn: () => api.setup.status(),
    staleTime: 0,
    retry: false,
  });

  useEffect(() => {
    // Only when claiming is actually possible — otherwise the redirect would
    // bounce the visitor to a page that can do nothing but send them back.
    if (setupStatus.data?.needsSetup && setupStatus.data.setupAllowed) {
      navigate('/setup', { replace: true });
    }
  }, [setupStatus.data, navigate]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(LoginSchema),
  });

  const onSubmit = async (data: LoginInput) => {
    setServerError(null);
    try {
      const res = (await api.auth.login(data.email, data.password)) as {
        user: Parameters<typeof login>[0];
        refreshToken: string;
      };
      login(res.user, res.refreshToken);
      navigate('/posts');
    } catch (err) {
      if (err instanceof ApiError) {
        setServerError(err.message);
      } else {
        setServerError('Something went wrong. Please try again.');
      }
    }
  };

  return (
    <div className="bg-bg text-ink min-h-screen">
      {/*
        Top bar: sits directly on the canvas with no bottom border, so it
        reads as floating in the room rather than as a separate band. The
        logomark is one of the few sanctioned uses of the indigo fill.
      */}
      <header className="mx-auto flex w-full max-w-[1200px] items-center justify-between gap-4 px-6 py-4">
        <div className="flex items-center gap-2.5">
          <span className="bg-accent text-accent-ink rounded-btn flex h-7 w-7 items-center justify-center">
            <PenSquare size={15} strokeWidth={2} />
          </span>
          <span className="text-[14px] font-semibold tracking-[0.04em] uppercase">BlogForger</span>
        </div>
        <span className="text-ink-soft font-mono text-[11px] tracking-[0.04em] uppercase">
          v0.1 · Private beta
        </span>
      </header>

      <main className="mx-auto w-full max-w-[1200px] px-6">
        {/*
          Hero. Centered single column on the canvas, and the only place the
          serif tier is allowed to speak. Exactly one emphasis word per line
          is set in italic — that upright/italic pairing IS the brand voice,
          so resist adding a second italic.
        */}
        <section className="mx-auto max-w-[760px] pt-20 pb-16 text-center">
          <p className="eyebrow">Your content, your rules</p>
          <h1 className="display mt-5 text-[clamp(2.5rem,5.2vw,4rem)]">
            Write, style and <em>publish</em> without the platform tax.
          </h1>
          <p className="text-ink-soft text-subheading mx-auto mt-5 max-w-[540px]">
            BlogForger turns drag-and-drop editing into clean, portable HTML with SEO baked in.
            Deploy the output anywhere you already host.
          </p>
        </section>

        {/* ── Sign-in card ────────────────────────────────────────────
            Paper White on Warm Bone, held by a Stone Mist hairline. Flat:
            the form is not a floating surface, so it casts no shadow. */}
        <section className="mx-auto w-full max-w-[460px]">
          <div className="glass-card p-8">
            <h2 className="text-heading-sm font-semibold">Welcome back</h2>
            <p className="text-ink-soft text-body-sm mt-1">
              Sign in to continue to your workspace.
            </p>

            {serverError && (
              <div
                role="alert"
                className="border-danger/40 text-danger text-body-sm rounded-btn mt-5 flex items-start gap-2.5 border px-3.5 py-3"
              >
                <span className="bg-danger mt-1.5 block h-1.5 w-1.5 shrink-0 rounded-full" />
                {serverError}
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-5">
              <div>
                <label htmlFor="email" className="field-label">
                  Email address
                </label>
                <input
                  id="email"
                  {...register('email')}
                  type="email"
                  autoComplete="email"
                  className="field-input"
                  placeholder="you@example.com"
                />
                {errors.email && (
                  <p className="text-danger mt-1.5 text-[12px]">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="field-label">
                  Password
                </label>
                <input
                  id="password"
                  {...register('password')}
                  type="password"
                  autoComplete="current-password"
                  className="field-input"
                />
                {errors.password && (
                  <p className="text-danger mt-1.5 text-[12px]">{errors.password.message}</p>
                )}
              </div>

              {/* The one chromatic action on the page. */}
              <button type="submit" disabled={isSubmitting} className="btn-primary w-full">
                {isSubmitting ? (
                  <>
                    <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    Signing in…
                  </>
                ) : (
                  'Sign in'
                )}
              </button>
            </form>

            {/*
              Card footer: hairline divider, mono micro-label left, arrow
              link right. This is the Arrow Link's home position — the only
              action in the footer, sitting flush right.
            */}
            <div className="border-line mt-6 flex items-center justify-between gap-4 border-t pt-4">
              <span className="text-ink-soft font-mono text-[11px] tracking-[0.04em] uppercase">
                14 days free
              </span>
              <a href="mailto:pelumi@commitshark.org" className="arrow-link">
                Request access →
              </a>
            </div>
          </div>
        </section>

        {/* ── Capability grid ───────────────────────────────────────── */}
        <section className="py-20">
          {/* .eyebrow is inline-flex, so it centres off the PARENT's
              text-align — not its own. */}
          <div className="text-center">
            <p className="eyebrow">Emails and posts, by big and small teams alike</p>
          </div>

          <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {CAPABILITIES.map((cap) => (
              <article key={cap.title} className="glass-card p-6">
                <h3 className="text-subheading font-semibold">{cap.title}</h3>
                <p className="text-ink-soft text-body-sm mt-2">{cap.body}</p>
                <div className="border-line mt-4 border-t pt-3">
                  <span className="text-ink-soft font-mono text-[11px] tracking-[0.04em] uppercase">
                    {cap.meta}
                  </span>
                </div>
              </article>
            ))}
          </div>
        </section>
      </main>

      {/* ── Footer ──────────────────────────────────────────────────── */}
      <footer className="border-line border-t">
        <div className="text-ink-soft mx-auto flex w-full max-w-[1200px] flex-wrap items-center justify-between gap-3 px-6 py-6 text-[12px]">
          <span className="inline-flex items-center gap-1.5">
            <ShieldCheck size={13} /> No third-party tracking
          </span>
          <span className="font-mono tracking-[0.04em] uppercase">BlogForger v0.1</span>
        </div>
      </footer>
    </div>
  );
}
